import { type FC } from "react";
import { useTranslation } from "react-i18next";
import {
  DefinitionTooltip,
  type DefinitionTooltipInfo,
} from "src/components/common";
import { calculateBarWidthPct } from "src/utils/chartBarScale";
import type { GoogleSectionHelpKey } from "./googleAdsTableConfig";

type FunnelStep = {
  kind: "currency" | "count" | "conversion";
  label: string;
  value: number;
  color: string;
};

interface GoogleConversionFunnelPanelProps {
  googleSectionHelp: (_key: GoogleSectionHelpKey) => DefinitionTooltipInfo;
  funnelSteps: FunnelStep[];
  formatCurrency: (
    _value: number,
    _options?: { minimumFractionDigits?: number; maximumFractionDigits?: number }
  ) => string;
  formatConversionMetric: (_value: number | null | undefined) => string;
  formatCountMetric: (_value: number | null | undefined) => string;
}

const GoogleConversionFunnelPanel: FC<GoogleConversionFunnelPanelProps> = ({
  googleSectionHelp,
  funnelSteps,
  formatCurrency,
  formatConversionMetric,
  formatCountMetric,
}) => {
  const { t } = useTranslation();
  const funnelValues = funnelSteps.map(item => item.value);
  const maxVal = Math.max(...funnelValues, 1);
  const minVal = Math.min(...funnelValues.filter(v => v > 0), 1);

  return (
    <article className="paid-media-panel">
      <div className="paid-media-panel__header">
        <div className="paid-media-panel__heading">
          <h3>{t("googleAdsReport.sections.conversionFunnel")}</h3>
          <DefinitionTooltip
            label={t("googleAdsReport.sections.conversionFunnel")}
            info={googleSectionHelp("conversionFunnel")}
            align="end"
            formulaTitle={t("webAnalytics.kpi.formula")}
          />
        </div>
      </div>
      <div className="paid-media-funnel">
        {funnelSteps.map(row => (
          <div className="paid-media-funnel__step" key={row.label}>
            <div className="paid-media-funnel__meta">
              <span>{row.label}</span>
              <strong>
                {row.kind === "currency"
                  ? formatCurrency(row.value, {
                      minimumFractionDigits: 0,
                      maximumFractionDigits: 0,
                    })
                  : row.kind === "conversion"
                    ? formatConversionMetric(row.value)
                    : formatCountMetric(row.value)}
              </strong>
            </div>
            <div className="paid-media-funnel__track">
              <div
                className="paid-media-funnel__fill"
                style={{
                  width: `${calculateBarWidthPct(row.value, maxVal, minVal, { mode: "log" })}%`,
                  background: row.color,
                }}
              />
            </div>
          </div>
        ))}
      </div>
    </article>
  );
};

export default GoogleConversionFunnelPanel;
